import {
  embedRefractionInExamOrderBody,
  refractionHasContent,
  type OphthalmologyRefractionValues,
} from './ophthalmologyRefractionConstants';

/** Lista padrão — solicitação de exames oftalmológicos (grade OD/OE/AO). */

export interface OphthalmologyExamRowDef {
  key: string;
  label: string;
  /**
   * Exames não vendidos de forma unilateral: apenas AO (ambos os olhos) é
   * marcável; OD/OE ficam desabilitados (sugestão médica — PDF 29.05).
   */
  aoOnly?: boolean;
}

export const OPHTHALMOLOGY_STANDARD_EXAMS: OphthalmologyExamRowDef[] = [
  { key: 'oct_macula', label: 'OCT Mácula' },
  { key: 'oct_nerve', label: 'OCT do Nervo' },
  { key: 'oct_nerve_macula', label: 'OCT Nervo e Mácula' },
  { key: 'ultrassonografia', label: 'Ultrassonografia' },
  { key: 'retinografia', label: 'Retinografia', aoOnly: true },
  { key: 'biometria_optica', label: 'Biometria Óptica' },
  { key: 'biometria_us', label: 'Biometria US' },
  { key: 'paquimetria', label: 'Paquimetria', aoOnly: true },
  { key: 'gonioscopia', label: 'Gonioscopia', aoOnly: true },
  { key: 'topografia_cornea', label: 'Topografia de Córnea', aoOnly: true },
  { key: 'pentacam', label: 'Pentacam' },
  { key: 'microscopia_especular', label: 'Microscopia Especular', aoOnly: true },
  { key: 'mapeamento_retina', label: 'Mapeamento de Retina', aoOnly: true },
  { key: 'curva_tensional', label: 'Curva Tensional (Minicurva)', aoOnly: true },
  { key: 'campo_visual_humphrey', label: 'Campo Visual Humphrey 10:2 e 24:2' },
];

export type OphthalmologyExamSelection = Record<
  string,
  { od: boolean; oe: boolean; ao: boolean }
>;

export function emptyOphthalmologySelections(): OphthalmologyExamSelection {
  return Object.fromEntries(
    OPHTHALMOLOGY_STANDARD_EXAMS.map((e) => [e.key, { od: false, oe: false, ao: false }]),
  );
}

/** Texto para anexar à evolução / impressão. */
export function buildOphthalmologyExamOrderBody(
  selections: OphthalmologyExamSelection,
  outrosText: string,
  observacoes: string,
  refraction?: OphthalmologyRefractionValues | null,
): string {
  const lines: string[] = [
    'SOLICITAÇÃO DE EXAMES — OFTALMOLOGIA',
    '',
  ];

  // Listagem numerada (1, 2, 3...) com a lateralidade ao lado de cada exame.
  let n = 0;
  for (const row of OPHTHALMOLOGY_STANDARD_EXAMS) {
    const s = selections[row.key];
    if (!s || (!s.od && !s.oe && !s.ao)) continue;
    const eyes: string[] = [];
    if (s.ao) {
      eyes.push('AO');
    } else {
      if (s.od) eyes.push('OD');
      if (s.oe) eyes.push('OE');
    }
    n += 1;
    lines.push(`${n}. ${row.label} (${eyes.join(' / ')})`);
  }

  // "Outros" entra na mesma numeração (uma linha por item informado).
  const outros = outrosText
    .split('\n')
    .map((t) => t.trim())
    .filter(Boolean);
  for (const item of outros) {
    n += 1;
    lines.push(`${n}. ${item}`);
  }

  lines.push('');
  const hasObsText = observacoes.trim().length > 0;
  const hasRefraction = refraction != null && refractionHasContent(refraction);
  if (hasObsText || hasRefraction) {
    lines.push('Observações:');
    if (hasRefraction) {
      lines.push('Refração (vide grade abaixo).');
    }
    if (hasObsText) {
      lines.push(observacoes.trim());
    }
    lines.push('');
  }

  lines.push('Legenda: OD = olho direito · OE = olho esquerdo · AO = ambos os olhos');

  return embedRefractionInExamOrderBody(lines.join('\n').trim(), refraction);
}
